/* eslint-disable react/jsx-no-useless-fragment */
import withAuthenticationRequired from '@/utils/jwt/withAuthenticationRequired';
import _, { isEqual } from 'lodash';
import mixpanel from 'mixpanel-browser';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { EVENT } from '@/analytics';
import ChromePopup from '@/components/ChromePopup';
import { CollectionTemplateSidebar as NewCollectionTemplateSidebar } from '@/components/Collection/CollectionTemplateSidebar';
import {
  CollectionTemplateContext,
  CollectionTemplateProvider,
} from '@/components/Collection/context/CollectionTemplateContextProvider';
import NewCollectionSettings from '@/components/CollectionSettings';
import { getCMSViewLimit } from '@/components/CollectionTable/utils';
import CustomDomainPopup from '@/components/CustomDomainPopup';
import { CustomDomainPopupProvider } from '@/components/CustomDomainPopup/context';
import EditingNavbar from '@/components/EditingNavbar';
import EditorWrapper from '@/components/EditorWrapper';
import { EmptyGuide } from '@/components/EmptyGuide';
import OverwritePopupNotice from '@/components/OverwritePopupNotice';
import { PageLinkEditorWrapper } from '@/components/PageLinkWrapper';
import SectionTemplateSidebar from '@/components/SectionTemplateSidebar';
import { EditorSidebar } from '@/components/Sidebar/EditorSidebar/EditorSidebar';
import UndoRedoComponent from '@/components/UndoRedo';
import {
  BACKUP_INTERVAL,
  BUCKET_URL,
  BUCKET_URL2,
  EDITOR_API_BASE_URL,
  LAST_BACKEDUP_DATA,
  LOGIN_PATH,
  SAVE_INTERVAL,
  SPACE_SUBSCRIPTIONS,
} from '@/constants';
import { api } from '@/data/api';
import { TextEditorProvider } from '@/data/editor/provider';
import { useSpaceAndPageIDFromRouter } from '@/data/space/hooks';
import CollectionSetupFlow from '@/sharedComponents/CollectionSetupFlow';
import {
  CollectionSetupFlowOpenContext,
  CollectionSetupFlowOpenDispatchContext,
} from '@/sharedComponents/CollectionSetupFlow/context/openContext';
import CollectionSetupFlowOpenProvider from '@/sharedComponents/CollectionSetupFlow/context/openProvider';
import { CMSConnectPopup } from '@/sharedComponents/TextEditor/components/CMSConnectPopup';
import LegacyCollectionSettings from '@/sharedComponents/TextEditor/components/CollectionSettings';
import LegacyCollectionTemplateSidebar from '@/sharedComponents/TextEditor/components/CollectionTemplateSidebar';
import useRedirectMobile from '@/utils/redirectMobile';

import {
  FormCreateOrSelect,
  FormSelectionPopup,
} from '@/sharedComponents/TextEditor/components/FormCreateOrSelect';
import TweetEncouragementPopup from '@/sharedComponents/TextEditor/components/TweetEncouragementPopup';
import { initialValueEmpty } from '@/sharedComponents/TextEditor/config';
import { MAX_FILE_SIZE } from '@/sharedComponents/TextEditor/constants';
import APIProvider from '@/sharedComponents/TextEditor/context/APIContextProvider';
import { CollectionPaywallContext } from '@/sharedComponents/TextEditor/context/CollectionPaywallContextProvider';
import { CollectionSettingsOpenDispatchContext } from '@/sharedComponents/TextEditor/context/CollectionSettingsContextProvider/openContext';
import CollectionSettingsOpenProvider from '@/sharedComponents/TextEditor/context/CollectionSettingsContextProvider/openProvider';
import { CollectionSettingsPathDispatchContext } from '@/sharedComponents/TextEditor/context/CollectionSettingsContextProvider/pathContext';
import CollectionSettingsPathProvider from '@/sharedComponents/TextEditor/context/CollectionSettingsContextProvider/pathProvider';
import { CollectionShowSchemaContext } from '@/sharedComponents/TextEditor/context/CollectionSettingsContextProvider/showSchemaContext';
import { EditorEditableProvider } from '@/sharedComponents/TextEditor/context/EditorEditableContextProvider';
import { SidebarProvider } from '@/sharedComponents/TextEditor/context/SidebarContextProvider';
import { TextEditorContext } from '@/sharedComponents/TextEditor/context/TextEditorContext';
import {
  XRayModeContext,
  XRayModeProvider,
} from '@/sharedComponents/TextEditor/context/XRayModeContextProvider';
import {
  useCreateTransclusionReferenceMutation,
  useFeatureFlag,
  useGetAllSyncedContainersQuery,
  useGetFormContentQuery,
  useGetPageQuery,
  useGetSpaceQuery,
  useListPagesQuery,
  usePagesPaths,
  useSaveContentMutation,
  useSaveNavbarMutation,
  useUpdatePageCustomizationMutation,
} from '@/store';
import styles from '@/styles/Home.module.css';
import { ICollectionCursorState } from '@/types';
import { pruneUnusedCollectionData } from '@/utils/collection';
import { createPageContentWithSpaceNavbar } from '@/utils/createPageContentWithNavbar';
import { errToString } from '@/utils/errors';
import isContentEqualNoID from '@/utils/isContentEqualNoID';
import TokenContext from '@/utils/jwt/tokenContext';
import updateTransclusion from '@/utils/updateTransclusion';
import useInterval from '@/utils/useInterval';
import useThemeFromSpace from '@/utils/useThemeFromSpace';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { CollectionElement as CollectionElementType, DreamValue, Navbar } from '@typedream/core';
import {
  App,
  ICollectionData,
  ICollectionDataItem,
  ICollectionDataItemMetadataKeys,
  ITransclusionReferenceItem,
  SUBSCRIPTION_PLAN,
} from '@typedream/data';
import {
  DreamEditor,
  ELEMENT_COLLECTION,
  ELEMENT_COLLECTION_ITEM,
  ELEMENT_CONTAINER,
  ELEMENT_FORM,
  ELEMENT_FORM_INPUT,
  ELEMENT_NAVBAR,
  ELEMENT_RICH_TEXT_CONTENT,
  KEY_AI,
  KEY_INTERNAL_LINK,
  KEY_SECTION_TEMPLATE,
} from '@typedream/dream-editor';
import { BaseDreamEditor } from '@typedream/dream-editor/src/types';
import MurmurHash from 'imurmurhash';
import isHotkey from 'is-hotkey';
import StableStringify from 'json-stable-stringify';
import { useTheme } from 'next-themes';
import useResizeObserver from 'use-resize-observer';
import { DreamEditorWrapper } from '@/components/DreamEditorWrapper';
import flags from '@/store/flags';

const TextEditor = dynamic(() => import('../../../../sharedComponents/TextEditor'));
const PageAndSpaceLoadingCircle = dynamic(
  () => import('../../../../components/PageAndSpaceLoadingCircle'),
  { ssr: false }
);

const editorID = 'main';

function LegacySpace() {
  const router = useRouter();
  const { slug } = router.query;
  const [spaceID, pageID] = useSpaceAndPageIDFromRouter();
  const { data: page } = useGetPageQuery([spaceID as string, pageID as string]);
  const { data: space } = useGetSpaceQuery([spaceID as string]);
  const { subscriptionTokenHandler } = useContext(TokenContext);

  useEffect(() => {
    mixpanel.track(EVENT.LOAD_PAGE);
  }, []);

  useEffect(() => {
    mixpanel.register({
      number_of_pages: (space?.routes?.children_keys || []).length + 1,
    });
  }, [space]);

  useEffect(() => {
    if (spaceID) {
      subscriptionTokenHandler.refreshSubscriptionToken(spaceID as string).then((sub) => {
        mixpanel.register({
          plan_type: SPACE_SUBSCRIPTIONS[sub.subscription.product_id],
        });
      });
    }
  }, [spaceID, subscriptionTokenHandler]);

  useEffect(() => {
    if (page?.customization?.item_page_data) {
      router.push(`/col/${slug}/${spaceID}/${pageID}/template`);
    }
  }, [page]);

  const [plan, setplan] = React.useState(SUBSCRIPTION_PLAN.FREE);

  // Check Subscription
  const checkSubscription = React.useCallback(async () => {
    if (spaceID) {
      try {
        const sub = await subscriptionTokenHandler.getSubscription(spaceID as string);
        setplan(sub?.getPlan(SPACE_SUBSCRIPTIONS));
      } catch (error) {
        setplan(SUBSCRIPTION_PLAN.FREE);
      }
    }
  }, [spaceID, subscriptionTokenHandler]);

  React.useEffect(() => {
    checkSubscription();
  }, [checkSubscription]);

  const { unlimited, limit } = getCMSViewLimit(plan);

  const isDreamEditorEnabled = useFeatureFlag(flags.DreamEditor);

  const CollectionSettings = isDreamEditorEnabled
    ? NewCollectionSettings
    : LegacyCollectionSettings;
  const CollectionTemplateSidebar = isDreamEditorEnabled
    ? NewCollectionTemplateSidebar
    : LegacyCollectionTemplateSidebar;

  return (
    <CollectionPaywallContext.Provider
      value={useMemo(() => ({ unlimited, limit }), [unlimited, limit])}
    >
      <div className={styles.container}>
        <EditorSidebar versionHistoryButton id={editorID} legacy />
        <ChromePopup />
        <OverwritePopupNotice />
        <CustomDomainPopup />
        <EditorWrapper>
          <EditingNavbar />
          <APIProvider
            assetsBaseURL={[BUCKET_URL, BUCKET_URL2]}
            documentBaseURL={EDITOR_API_BASE_URL}
            errToString={errToString}
          >
            <PageLinkEditorWrapper>
              <TextEditor id={editorID}>
                <CollectionTemplateSidebar />

                <CollectionSettings plan={plan} />
                <CollectionSetupFlow connectElement allowConnectIfConnected />
                <FormCreateOrSelect />
                <CMSConnectPopup />

                <TweetEncouragementPopup />
              </TextEditor>
            </PageLinkEditorWrapper>
          </APIProvider>
        </EditorWrapper>
      </div>
    </CollectionPaywallContext.Provider>
  );
}

const StyledCanvas = styled.main`
  position: relative;
  width: 100%;
`;

const StyledRightSidebar = styled.aside`
  position: relative;
  flex: 300px 0;
  overflow-y: auto;
  border-left: 0.5px solid var(--color-border-light);
  background-color: var(--background);
`;

function Space() {
  const router = useRouter();
  const { slug } = router.query;
  const [spaceID, pageID] = useSpaceAndPageIDFromRouter();
  const { theme } = useTheme();
  useThemeFromSpace(spaceID);

  const { data: page, isLoading } = useGetPageQuery([spaceID as string, pageID as string]);
  const { data: space, error: errSpace } = useGetSpaceQuery([spaceID as string]);
  const {
    sidebarOpen: sidebarTemplateOpen,
    setsidebarOpen: setsidebarTemplateOpen,
    templatePath,
    settemplatePath,
    selectedTemplate,
    selectedCategory,
    hovered,
  } = useContext(CollectionTemplateContext);
  const isXrayMode = useContext(XRayModeContext);
  const showSchema = useContext(CollectionShowSchemaContext);
  const settingsOpen = useContext(CollectionSetupFlowOpenContext);
  const setsettingsOpen = useContext(CollectionSetupFlowOpenDispatchContext);
  const setcollectionSettingsPath = useContext(CollectionSettingsPathDispatchContext);
  const setcollectionSettingsOpen = useContext(CollectionSettingsOpenDispatchContext);
  const { spacePermissionTokenHandler, subscriptionTokenHandler } = useContext(TokenContext);
  const [visitorFilterList, setVisitorFilterList] = React.useState<string[]>([]);
  const [collectionSynced, setcollectionSynced] = useState(false);
  const [nextDataLoading, setnextDataLoading] = React.useState(false);
  const collectionData = page?.customization?.collection_data;
  const [mounted, setMounted] = useState(false);
  const [isDraft, setIsDraft] = useState(false);
  const { mutate: saveNavbar, error: errorSaveNavbar } = useSaveNavbarMutation({
    fixedCacheKey: `saveNavbar-${spaceID}`,
  });

  // this is required so usePagesPaths will work. FIXME?
  useListPagesQuery([spaceID as string]);
  const pagesPaths = usePagesPaths(spaceID as string);

  const currentValueRef = React.useRef<DreamValue>(null);

  /**
   * Used to track the changes of the navbar in the page's content.
   * Then, when we save the page, this value will be used to update
   * the navbar in the `space` object.
   */
  const currentNavbarRef = React.useRef<Navbar>(null);

  const isDreamEditorEnabled = useFeatureFlag(flags.DreamEditor);

  const CollectionSettings = isDreamEditorEnabled
    ? NewCollectionSettings
    : LegacyCollectionSettings;
  const CollectionTemplateSidebar = isDreamEditorEnabled
    ? NewCollectionTemplateSidebar
    : LegacyCollectionTemplateSidebar;

  useEffect(() => {
    if (space) {
      currentNavbarRef.current = space.navbar;
    }
  }, [space]);

  useEffect(() => {
    if (!mounted) {
      currentValueRef.current = initialValueEmpty;
    }
  }, [mounted]);

  useEffect(() => {
    if (!mounted) setMounted(true);
    mixpanel.track(EVENT.LOAD_PAGE);
  }, [pageID]);

  useEffect(() => {
    if (page && page.content) {
      setIsDraft(!isContentEqualNoID(currentValueRef.current, page.content));
    }
    if (page?.customization?.item_page_data) {
      router.push(`/col/${slug}/${spaceID}/${pageID}/template`);
    }
  }, [page]);

  const getTransclusionReferences = async () => {
    const c = await useGetAllSyncedContainersQuery.run([spaceID as string]);
    if (c.error || !c.data) {
      console.error('unable to get transclusion reference', c.error);
    }
    return c.data;
  };

  const { mutate: createTransclusionReference } = useCreateTransclusionReferenceMutation();

  useEffect(() => {
    const handleRouteChange = (url) => {
      setMounted(url === router.asPath);
    };

    router.events.on('routeChangeStart', handleRouteChange);

    return () => {
      router.events.off('routeChangeStart', handleRouteChange);
    };
  }, [router.events, router.asPath]);

  // *************************************************************
  // START OF SAVING MECHANISM
  // TODOs:
  // - [ ] Save navbar
  // - [ ] Backups
  // *************************************************************

  // This ref is used to track saveAll call. When there is a pending saveAll,
  // the next saveAll invokation will returns early without further API request.
  // This is to prevent "Use My Changes" pop up keeps prompting.
  const isPendingSaveAllRef = useRef(false);

  const initialValue = createPageContentWithSpaceNavbar({
    pageContent: [
      updateTransclusion(space?.customization?.transclusion_reference || {})(
        page?.content?.[0] || initialValueEmpty[0]
      ),
    ],
    space,
  });

  const { mutate: saveContent, isError: isErrorSaveContent } = useSaveContentMutation({
    fixedCacheKey: `saveContent-${pageID}`,
  });

  const getCurrentPageIsLoading = useCallback(
    () => !!useGetPageQuery.get(spaceID as string, pageID as string)?.isLoading,
    [pageID, spaceID]
  );

  const getCurrentPage = useCallback(
    () => useGetPageQuery.get(spaceID as string, pageID as string)?.data,
    [pageID, spaceID]
  );

  const getCurrentSpaceIsLoading = useCallback(
    () => useGetSpaceQuery.get(spaceID as string)?.isLoading,
    [spaceID]
  );

  const saveLastBackupHashLocally = (
    currSlug: string,
    spaceId: string,
    pageId: string,
    contentHash: string
  ) => {
    const lastBackup = localStorage.getItem(LAST_BACKEDUP_DATA);
    const parsedBackup = JSON.parse(lastBackup);
    const newBackupLocal = {
      ...parsedBackup,
      [currSlug]: {
        ...parsedBackup?.[currSlug],
        [spaceId]: {
          ...parsedBackup?.[currSlug]?.[spaceId],
          [pageId]: contentHash,
        },
      },
    };
    localStorage.setItem(LAST_BACKEDUP_DATA, JSON.stringify(newBackupLocal));
  };

  const getLastBackupHash = (currSlug: string, spaceId: string, pageId: string) => {
    const lastBackup = localStorage.getItem(LAST_BACKEDUP_DATA);
    const parsedBackup = JSON.parse(lastBackup);
    return parsedBackup?.[currSlug]?.[spaceId]?.[pageId];
  };

  const saveBackup = useCallback(async () => {
    const valueToBeSaved = StableStringify(currentValueRef.current);
    const valueToBeSavedHash = MurmurHash(valueToBeSaved).result().toString();
    const lastBackupHash = getLastBackupHash(slug as string, spaceID as string, pageID as string);
    if (valueToBeSavedHash !== lastBackupHash && space) {
      api()
        .history()
        .backup(spaceID as string, pageID as string, valueToBeSaved)
        .then(() =>
          saveLastBackupHashLocally(
            space.slug,
            spaceID as string,
            pageID as string,
            valueToBeSavedHash
          )
        )
        .catch(() => null);
    }
  }, [slug, spaceID, pageID, space]);

  const saveAll = useCallback(
    async (options?: {
      shouldSaveContent?: boolean;
      forceSaveContent?: boolean;
      shouldSaveNavbar?: boolean;
      forceSaveNavbar?: boolean;
      // value is used to save the content when onRouteChangeStart is triggered
      // if this value isn't set, it'll use currentValueRef
      value?: DreamValue;
    }) => {
      const { shouldSaveContent, forceSaveContent, value } = options || {};

      // pages API
      const savePages = async () => {
        const currentCollectionData = getCurrentPage()?.customization?.collection_data;
        if (currentCollectionData && (value || currentValueRef.current)) {
          // prune the collection item data
          const prunedCollectionData = pruneUnusedCollectionData(
            currentCollectionData,
            value || currentValueRef.current
          );

          // check if the keys of collection data changed
          const collectionDataKeysChanged = !isEqual(
            Object.keys(currentCollectionData),
            Object.keys(prunedCollectionData)
          );

          if (collectionDataKeysChanged) {
            // update page collection data
            useUpdatePageCustomizationMutation.run([
              {
                spaceID: spaceID as string,
                pageID: pageID as string,
                customizationUpdateRequests: [
                  {
                    customization_path: ['collection_data'],
                    customization_value: prunedCollectionData,
                  },
                ],
              },
            ]);
          }
        }

        if (shouldSaveContent) {
          await saveContent({
            spaceID: spaceID as string,
            pageID: pageID as string,
            newContent: value || currentValueRef.current,
            force: forceSaveContent,
          });
        }

        if (shouldSaveContent) {
          await useGetPageQuery.run([spaceID as string, pageID as string]);
        }
      };

      if (isPendingSaveAllRef.current) {
        return;
      }

      isPendingSaveAllRef.current = true;

      if ((currentValueRef.current as unknown as Navbar) !== space?.navbar) {
        await saveNavbar({
          spaceID: spaceID as string,
          newNavbar: currentNavbarRef.current,
          force: true,
        });
      }

      // pages API and spaces API doesn't need to wait for each other
      // so we can use Promise.all to save them in parallel
      // but each requests inside savePages or saveSpaces need to run in sequence
      await Promise.all([savePages()]);

      isPendingSaveAllRef.current = false;
    },
    [pageID, saveContent, spaceID, getCurrentPage]
  );

  // Handle CMD+S
  useEffect(() => {
    const handleSave = (e: KeyboardEvent): void => {
      if (!isHotkey('mod+s')(e)) return;
      e.preventDefault();
      saveAll({
        shouldSaveContent: true,
      });
    };

    window.addEventListener('keydown', handleSave);
    return () => {
      window.removeEventListener('keydown', handleSave);
    };
  }, [saveAll]);

  useEffect(() => {
    if (!space) {
      return () => {
        /* do nothing */
      };
    }

    const warningText = 'You have unsaved changes - are you sure you wish to leave this page?';

    function confirmExit() {
      if (isDraft) return warningText;
      return null;
    }

    async function onRouteChangeStart() {
      const value = currentValueRef.current;

      const shouldSave =
        pageID &&
        isDraft &&
        !isEqual(value, initialValueEmpty) &&
        !getCurrentSpaceIsLoading() &&
        !getCurrentPageIsLoading();

      saveAll({
        shouldSaveContent: shouldSave,
        forceSaveContent: true,
        value,
      });

      // if page is not yet backed up, back up now
      if (!getLastBackupHash(slug as string, spaceID as string, pageID as string)) {
        saveBackup();
      }
    }

    window.addEventListener('beforeunload', confirmExit);
    router.events.on('routeChangeStart', onRouteChangeStart);

    return () => {
      window.removeEventListener('beforeunload', confirmExit);
      router.events.off('routeChangeStart', onRouteChangeStart);
    };
  }, [
    page,
    saveBackup,
    spaceID,
    space,
    pageID,
    router.events,
    getCurrentSpaceIsLoading,
    getCurrentPageIsLoading,
    slug,
    saveContent,
    saveAll,
    isDraft,
  ]);

  // Handle scaling down the editor when the canvas size is smaller than 1024px
  const mainCanvasRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);

  useResizeObserver({
    ref: mainCanvasRef,
    onResize: ({ width }) => {
      // 1024 is the actual breakpoint for desktop
      // 1048 is 1024 + 24px padding to accomodate for missing navbar
      if (width <= 1048) {
        setScale(width / 1048);
      }
    },
  });

  // direct port from apps/editor/data/editor/provider.tsx
  // there's no transclusionReference saves in the new editor
  const savePageAndNavbarAndTransclusionReference = useCallback(
    async (force = false) => {
      const value = currentValueRef.current;

      const shouldSaveContent =
        pageID && isDraft && !isEqual(value, initialValueEmpty) && !getCurrentPageIsLoading();

      await saveAll({
        shouldSaveContent,
        forceSaveContent: force,
      });
    },
    [pageID, getCurrentPageIsLoading, isDraft, saveAll]
  );

  useInterval(savePageAndNavbarAndTransclusionReference, SAVE_INTERVAL * 1000);
  useInterval(saveBackup, BACKUP_INTERVAL * 1000);
  // *************************************************************
  // END OF SAVING MECHANISM
  // *************************************************************

  useEffect(() => {
    setcollectionSynced(false);
  }, [pageID]);

  const legacyProviderValues = useMemo(
    () => ({
      initialValue: [],
      setInitialValue: undefined,
      isDraft,
      loading: false,
      save: () => {
        saveAll({
          shouldSaveContent: true,
        });
      },
    }),
    [isDraft, saveAll]
  );
  const [plan, setplan] = React.useState(SUBSCRIPTION_PLAN.FREE);

  // Check Subscription
  const checkSubscription = React.useCallback(async () => {
    if (spaceID) {
      try {
        const sub = await subscriptionTokenHandler.getSubscription(spaceID as string);
        setplan(sub?.getPlan(SPACE_SUBSCRIPTIONS));
      } catch (error) {
        setplan(SUBSCRIPTION_PLAN.FREE);
      }
    }
  }, [spaceID, subscriptionTokenHandler]);

  React.useEffect(() => {
    checkSubscription();
  }, [checkSubscription]);

  const { unlimited, limit } = getCMSViewLimit(plan);

  const cmsPaywallValue = useMemo(() => ({ unlimited, limit }), [unlimited, limit]);
  if (!page || !space || !mounted) return <PageAndSpaceLoadingCircle />;

  const getFormContent = async (formID: string) => {
    const c = await useGetFormContentQuery.run([spaceID as string, formID]);
    if (c.error || !c.data) {
      console.error('unable to get form content', c.error);
      return [];
    }
    return c.data;
  };
  const handleGoFormSubmissions = (formID: string) => {
    if (!formID) return;
    router.push(`/settings/${slug}/${spaceID}/forms/${formID}?tab=submission`);
  };
  const handleGoEditForm = (formID: string) => {
    if (!formID) return;
    router.push(`/settings/${slug}/${spaceID}/forms/${formID}`);
  };

  const getVisitorFilterList = async (collDataItem: ICollectionDataItem) => {
    const app = new App(api().api);
    const cols = await app
      .app(collDataItem)
      ?.getColumnSelectOptions(
        spaceID as string,
        collDataItem.metadata.visitor_filter_column,
        collDataItem,
        spacePermissionTokenHandler,
        subscriptionTokenHandler
      );
    setVisitorFilterList(cols);
  };

  const getIsTableConnected = (collDataItem: ICollectionDataItem) => {
    const app = new App(api().api);
    const isConnected = app.app(collDataItem)?.tableConnected(collDataItem);
    return isConnected;
  };

  const getItemPageDataID = (collDataItem: ICollectionDataItem) => {
    const app = new App(api().api);
    return app.app(collDataItem)?.getItemPageDataID(collDataItem);
  };

  const getNextData = async (
    synchronize: boolean,
    collDataItem: ICollectionDataItem,
    cursorStates: ICollectionCursorState,
    setCursorState: (elementID: string, cursorStates: ICollectionCursorState) => void,
    editor: BaseDreamEditor,
    element: CollectionElementType,
    synchronizeCollection: (pageCollData: ICollectionData, elementID?: string) => void
  ) => {
    const elementID = element.id;
    const dataID = element.collection_data_id;

    setnextDataLoading(true);
    const app = new App(api().api);
    const currCursor = cursorStates?.nextCursor;
    const currSearchQuery = cursorStates?.searchQuery;
    const currData = cursorStates?.data || [];
    const itemsPerPage = collDataItem?.metadata?.[ICollectionDataItemMetadataKeys.MAX_ITEMS];
    if (
      app.app(collDataItem)?.tableConnected(collDataItem) &&
      currCursor !== cursorStates?.fetchedCursor
    ) {
      const dataResults = await app
        .app(collDataItem)
        ?.getData(
          spaceID as string,
          collDataItem,
          currCursor || undefined,
          spacePermissionTokenHandler,
          subscriptionTokenHandler
        );

      const resData = dataResults?.data || [];
      const newData = [...currData, ...resData];
      const newState: ICollectionCursorState = {
        hasMore: dataResults?.has_more,
        nextCursor: dataResults?.next_cursor,
        fetchedCursor: currCursor,
        data: newData,
        itemsPerPage,
        err: null,
        searchQuery: currSearchQuery,
      };

      setCursorState(elementID, newState);

      if (
        synchronize &&
        newData &&
        newData?.length > 0 &&
        editor?.children &&
        editor.children !== initialValueEmpty &&
        elementID
      ) {
        const newCollectionDataItem = {
          ...collDataItem,
          data: newData,
        };
        const newCollectionData = {
          ...collectionData,
          [dataID]: newCollectionDataItem,
        };

        synchronizeCollection(newCollectionData);
      }
    }

    setnextDataLoading(false);
  };

  // TODO: this is a hack to get the navbar to save. This solution is not ideal
  // since it may poluted the network requests.
  const debounceSaveNavbar = _.debounce(async () => {
    await saveNavbar({
      spaceID: spaceID as string,
      newNavbar: currentNavbarRef.current,
      force: true,
    });
  }, 1000);

  /**
   * Update `currentNavbarRef` when navbar is changed.
   * @param next
   */
  const handleOnChangeNavbar = (next: DreamValue) => {
    const nextFirstChildren = next?.[0]?.children?.[0];
    if (nextFirstChildren?.type === ELEMENT_NAVBAR) {
      currentNavbarRef.current = nextFirstChildren as unknown as Navbar;
    } else {
      currentNavbarRef.current = null;
    }
    debounceSaveNavbar();
  };

  return (
    <DreamEditor.Provider
      id={editorID}
      initialContent={initialValue}
      pluginsOverride={{
        [KEY_AI]: false,
        [ELEMENT_FORM]: {
          options: {
            isEditable: false,
            getFormContent,
            toolbarActions: {
              handleGoFormSubmissions,
              handleGoEditForm,
            },
          },
        },
        [ELEMENT_FORM_INPUT]: false,
        [KEY_SECTION_TEMPLATE]: {
          options: {
            format: {
              onInsert: (editor, location) => {
                editor.emitter.emit('sidebar:toggle-section-template', location);
              },
            },
          },
        },
        [ELEMENT_CONTAINER]: {
          options: {
            // NEED TO CALL DIRECTLY FROM DATA LAYER.
            // SAME CASE WITH FORMS.
            getTransclusionReferences,
            isTransclusionEnabled: true,
            createTransclusionReference: (
              transclusionID: string,
              transclusionItem: ITransclusionReferenceItem
            ) => {
              createTransclusionReference(spaceID as string, transclusionID, transclusionItem);
            },
            onEditSyncedReference: (syncedID: string) => {
              router.push(`/s/${slug}/${spaceID}/tsc/s/${syncedID}`);
            },
          },
        },
        [ELEMENT_COLLECTION]: {
          options: {
            collectionData,
            sidebarTemplateOpen,
            setsidebarTemplateOpen,
            settemplatePath,
            templatePath,
            settingsOpen,
            setsettingsOpen,
            getVisitorFilterList,
            visitorFilterList,
            setVisitorFilterList,

            getIsTableConnected,
            showSchema,

            setcollectionSettingsPath,

            getNextData,

            getItemPageDataID,
            selectedTemplate,
            nextDataLoading,
            setnextDataLoading,
            selectedCategory,
            hovered,
            space,
            loadingPage: isLoading,
            setcollectionSettingsOpen,

            initialValueEmpty,
            collectionSynced,
            setcollectionSynced,
            isContentEqualNoID,

            unlimited,
            limit,
          },
        },
        [ELEMENT_RICH_TEXT_CONTENT]: false,
        [ELEMENT_COLLECTION_ITEM]: {
          options: {
            space,
          },
        },
        [KEY_INTERNAL_LINK]: {
          options: {
            pagesPaths,
          },
        },
      }}
      fileManager={{
        upload: (file) => {
          if (file.size > MAX_FILE_SIZE) {
            throw new Error(`Your file size cannot be bigger than ${MAX_FILE_SIZE / 10 ** 6}MB`);
          }

          const documentAPI = api(EDITOR_API_BASE_URL).document();

          return documentAPI
            .upload(file, spaceID as string, spacePermissionTokenHandler, subscriptionTokenHandler)
            .then((payload) => {
              const filename = payload.data.file_name;
              return documentAPI.getURL(filename);
            });
        },
      }}
      onChange={(value) => {
        setIsDraft(!isContentEqualNoID(value, initialValue));
        handleOnChangeNavbar(value);
        currentValueRef.current = value;
      }}
      onSave={() => {
        saveAll({
          shouldSaveContent: true,
        });
      }}
    >
      <TextEditorContext.Provider value={legacyProviderValues}>
        <CollectionPaywallContext.Provider value={cmsPaywallValue}>
          <UndoRedoComponent />
          <SectionTemplateSidebar />
          <ChromePopup />
          <OverwritePopupNotice save={savePageAndNavbarAndTransclusionReference} />
          <CustomDomainPopup />
          <EditorSidebar versionHistoryButton id={editorID} />
          <div
            css={css`
              display: flex;
              flex-direction: column;
              flex-grow: 1;
            `}
          >
            <CollectionTemplateSidebar />
            <CollectionSetupFlow connectElement allowConnectIfConnected />
            <EditingNavbar />
            <TweetEncouragementPopup />
            <FormSelectionPopup editorID={editorID} />
            <CollectionSettings />
            <DreamEditorWrapper>
              <StyledCanvas ref={mainCanvasRef}>
                <DreamEditor.Canvas theme={theme} isXrayMode={isXrayMode} scale={scale}>
                  <DreamEditor.OverlayToolbar
                    disablePageSettings={false}
                    isNavbarExist={!!space?.navbar}
                  />
                </DreamEditor.Canvas>
                <EmptyGuide />
              </StyledCanvas>
              <StyledRightSidebar>
                <DreamEditor.SideToolbar />
              </StyledRightSidebar>
            </DreamEditorWrapper>
          </div>
        </CollectionPaywallContext.Provider>
      </TextEditorContext.Provider>
    </DreamEditor.Provider>
  );
}

function Comp() {
  useRedirectMobile();
  return (
    <XRayModeProvider>
      <SidebarProvider>
        <CustomDomainPopupProvider>
          <CollectionTemplateProvider>
            <CollectionSetupFlowOpenProvider>
              <CollectionSettingsOpenProvider>
                <CollectionSettingsPathProvider>
                  <div className="flex h-screen relative">
                    <Space />
                  </div>
                </CollectionSettingsPathProvider>
              </CollectionSettingsOpenProvider>
            </CollectionSetupFlowOpenProvider>
          </CollectionTemplateProvider>
        </CustomDomainPopupProvider>
      </SidebarProvider>
      {/* <PageAndSpaceLoadingCircle /> */}
    </XRayModeProvider>
  );
}

function LegacyEditor() {
  return (
    <APIProvider
      assetsBaseURL={[BUCKET_URL, BUCKET_URL2]}
      documentBaseURL={EDITOR_API_BASE_URL}
      errToString={errToString}
    >
      <EditorEditableProvider editable>
        <TextEditorProvider editorID={editorID}>
          <XRayModeProvider>
            <SidebarProvider>
              <CustomDomainPopupProvider>
                <LegacySpace />
              </CustomDomainPopupProvider>
            </SidebarProvider>
          </XRayModeProvider>
          <PageAndSpaceLoadingCircle />
        </TextEditorProvider>
      </EditorEditableProvider>
    </APIProvider>
  );
}

export default withAuthenticationRequired(
  () => {
    const isDreamEditorEnabled = useFeatureFlag(flags.DreamEditor);

    return isDreamEditorEnabled ? (
      <Comp />
    ) : (
      <>
        <LegacyEditor />
      </>
    );
  },
  {
    loginPagePath: LOGIN_PATH,
  }
);
